import axios from "axios";
import { endpoints } from "@/configs/Settings";
import { useAuth } from "@/utils/useAuth";
import { refreshGoogleFitAccessToken } from "./refreshGoogleFitAccessToken";
import {
  isGoogleHealthScopeError,
  toGoogleHealthScopeError,
} from "./googleHealthErrors";
import { normalizeDataPointsListResponse } from "./normalizeDataPointsResponse";
import type {
  IFetchGoogleHealthDataPointsParams,
  IGoogleHealthDataPoint,
  IListGoogleHealthDataPointsResponse,
} from "./types";

const DEFAULT_PAGE_SIZE = 1000;

async function buildAuthHeaders(accessTokenGoogle: string) {
  return {
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/json",
    "x-custom-google-auth": `Bearer ${accessTokenGoogle}`,
  };
}

function dataPointsPath(
  dataType: IFetchGoogleHealthDataPointsParams["dataType"]
): string {
  return `${endpoints.GOOGLE_HEALTH}/v4/users/me/dataTypes/${dataType}/dataPoints`;
}

async function listPage(
  dataType: IFetchGoogleHealthDataPointsParams["dataType"],
  accessTokenGoogle: string,
  filter: string,
  pageSize: number,
  pageToken?: string
): Promise<IListGoogleHealthDataPointsResponse> {
  const params: Record<string, string | number> = {
    filter,
    pageSize,
  };
  if (pageToken) params.pageToken = pageToken;

  const response = await axios.get<IListGoogleHealthDataPointsResponse>(
    dataPointsPath(dataType),
    {
      headers: await buildAuthHeaders(accessTokenGoogle),
      params,
    }
  );

  return normalizeDataPointsListResponse(response.data);
}

/**
 * Lista todos os data points de um tipo, com paginação e retry em 401.
 * No 401, renova com client Web ou mobile (quem gerou o refresh token).
 */
export async function getGoogleHealthDataPoints(
  params: IFetchGoogleHealthDataPointsParams,
  alreadyRetried = false
): Promise<IGoogleHealthDataPoint[]> {
  const {
    dataType,
    filter,
    pageSize = DEFAULT_PAGE_SIZE,
    accessTokenGoogle,
    refreshToken,
    setAccessTokenGoogle,
  } = params;

  let access = accessTokenGoogle;
  const collected: IGoogleHealthDataPoint[] = [];
  let pageToken: string | undefined;

  try {
    do {
      const page = await listPage(dataType, access, filter, pageSize, pageToken);
      if (Array.isArray(page.dataPoints)) {
        collected.push(...page.dataPoints);
      }
      pageToken =
        typeof page.nextPageToken === "string" && page.nextPageToken
          ? page.nextPageToken
          : undefined;
    } while (pageToken);

    return collected;
  } catch (error: unknown) {
    if (isGoogleHealthScopeError(error)) {
      throw toGoogleHealthScopeError(error);
    }

    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    if (status === 401 && !alreadyRetried) {
      const tokenData = await refreshGoogleFitAccessToken(refreshToken);
      access = tokenData.access_token;
      setAccessTokenGoogle(access);
      return getGoogleHealthDataPoints(
        { ...params, accessTokenGoogle: access },
        true
      );
    }
    throw error;
  }
}
